import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ArrowLeftRight, TrendingUp, Palmtree, FolderOpen,
  ClipboardCheck, BarChart3, History, Settings, Construction, Download, Upload, Search, X, Plus, UserCog,
  Building2, Crown, Hourglass, CheckCircle2, XCircle, Archive,
} from "lucide-react";
import client, { API, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { EMRequestsPage, EMApprovals, EMArchive, EMEmployeeArchive, EMAudit, EMFiles, TrialBadge } from "@/components/EMSections";
import { EMFieldsManager } from "@/components/SystemSettings";
import EmployeeDetails from "@/components/EmployeeDetails";
import EmployeeForm from "@/components/EmployeeForm";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { usePerms } from "@/lib/perms";

export const EM_SECTIONS = [
  { key: "dashboard", path: "/employee-management", icon: LayoutDashboard, labelKey: "em_nav_dashboard" },
  { key: "employees", path: "/employee-management/employees", icon: Users, labelKey: "em_nav_employees" },
  { key: "transfers", path: "/employee-management/transfers", icon: ArrowLeftRight, labelKey: "em_nav_transfers" },
  { key: "promotions", path: "/employee-management/promotions", icon: TrendingUp, labelKey: "em_nav_promotions" },
  { key: "vacations", path: "/employee-management/vacations", icon: Palmtree, labelKey: "em_nav_vacations" },
  { key: "files", path: "/employee-management/files", icon: FolderOpen, labelKey: "em_nav_files" },
  { key: "approvals", path: "/employee-management/approvals", icon: ClipboardCheck, labelKey: "em_nav_approvals" },
  { key: "reports", path: "/employee-management/reports", icon: BarChart3, labelKey: "em_nav_reports" },
  { key: "archive", path: "/employee-management/archive", icon: Archive, labelKey: "em_nav_archive" },
  { key: "audit", path: "/employee-management/audit", icon: History, labelKey: "em_nav_audit" },
  { key: "settings", path: "/employee-management/settings", icon: Settings, labelKey: "em_nav_settings" },
];

function PlaceholderCard({ section, t }) {
  const Icon = section.icon;
  return (
    <div className="bg-white rounded-2xl card-shadow p-10 text-center" data-testid={`em-placeholder-${section.key}`}>
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg mb-4">
        <Icon size={30} />
      </div>
      <h2 className="text-xl font-black text-slate-800 mb-2">{t(section.labelKey)}</h2>
      <div className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1 mb-3">
        <Construction size={12} /> {t("em_placeholder_badge")}
      </div>
      <p className="text-sm text-slate-500 max-w-md mx-auto">{t("em_placeholder_text")}</p>
    </div>
  );
}

function EMStaffSection({ row, t, isSuper, onFlagsChanged }) {
  const [fields, setFields] = useState([]);
  const [flags, setFlags] = useState(row.em_flags || {});
  const [manage, setManage] = useState(false);
  const [newName, setNewName] = useState("");
  const loadFields = () => client.get("/em/fields").then((r) => setFields(r.data || [])).catch(() => {});
  useEffect(() => { loadFields(); }, []);

  const toggle = async (fid) => {
    const next = { ...flags, [fid]: !flags[fid] };
    setFlags(next);
    try {
      await client.patch(`/em/employees/${row.id}`, { em_flags: { [fid]: !flags[fid] } });
      onFlagsChanged && onFlagsChanged(row.id, next);
    } catch { setFlags(flags); toast.error(t("em_save_error")); }
  };
  const addField = async () => {
    if (!newName.trim()) return;
    try { await client.post("/em/fields", { name: newName.trim() }); setNewName(""); loadFields(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };
  const delField = async (fid) => {
    try { await client.delete(`/em/fields/${fid}`); loadFields(); }
    catch { toast.error(t("em_save_error")); }
  };
  const setFieldLevel = async (f) => {
    const next = f.alert_level === "critical" ? "normal" : "critical";
    try { await client.patch(`/em/fields/${f.id}`, { alert_level: next }); loadFields(); }
    catch { toast.error(t("em_save_error")); }
  };

  const info = [["COMPANY", row.company], ["NATIONALITY", row.nationality], ["OLD ID", row.old_id], ["City", row.city], ["B Type", row.branch_type], ["DATE", row.date], ["NOTE", row.note]];
  return (
    <>
      <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 card-shadow">
        <h4 className="text-xs font-bold text-gray-400 mb-2">Staff Update</h4>
        {info.map(([label, val]) => (
          <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 text-sm">
            <span className="text-gray-500 text-xs font-semibold">{label}</span>
            <span className="text-gray-900 font-semibold" style={{ direction: "ltr" }} data-testid={`em-detail-${label.toLowerCase().replace(/ /g, "-")}`}>{val || "—"}</span>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 card-shadow" data-testid="em-custom-fields-section">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold text-gray-400">{t("em_custom_fields")}</h4>
          {isSuper && (
            <button onClick={() => setManage(true)} className="text-[11px] font-bold text-sky-600 hover:underline" data-testid="em-manage-fields-btn">
              ⚙️ {t("em_manage_fields")}
            </button>
          )}
        </div>
        {fields.length === 0 ? (
          <div className="text-xs text-gray-400 py-2" data-testid="em-no-fields">{t("em_no_fields")}</div>
        ) : (
          <>
            <div className="mb-1 pb-2 border-b border-gray-100">
              <div className="text-[10px] font-bold text-slate-400 mb-1">{t("em_last_data")}</div>
              {[["DATE", row.date], ["NOTE", row.note]].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-gray-500 text-xs font-semibold">{label}</span>
                  <span className="text-gray-900 font-semibold" style={{ direction: "ltr" }} data-testid={`em-extra-${label.toLowerCase()}`}>{val || "—"}</span>
                </div>
              ))}
            </div>
            {fields.map((f) => (
              <div key={f.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 text-sm">
                <span className="text-gray-700 font-semibold inline-flex items-center gap-1.5">
                  {f.name}
                  {f.alert_level === "critical" && (
                    <span className="text-[9px] font-black bg-rose-100 text-rose-600 rounded-full px-1.5 py-0.5" data-testid={`em-field-critical-badge-${f.id}`}>🚨 {t("em_field_level_critical")}</span>
                  )}
                </span>
                <button onClick={() => toggle(f.id)}
                  className={`px-3 py-1 rounded-full text-[11px] font-bold transition ${flags[f.id] ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                  data-testid={`em-flag-${f.id}`}>
                  {flags[f.id] ? `✓ ${t("yes")}` : `— ${t("no")}`}
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {manage && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setManage(false)} data-testid="em-fields-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-800 text-sm">{t("em_manage_fields")}</h3>
              <button onClick={() => setManage(false)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="em-fields-close"><X size={16} /></button>
            </div>
            <div className="flex gap-2">
              <input value={newName} onChange={(ev) => setNewName(ev.target.value)} placeholder={t("em_field_name_ph")} className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="em-new-field-input" />
              <button onClick={addField} className="bg-sky-600 text-white text-xs font-bold rounded-xl px-4 hover:bg-sky-700" data-testid="em-add-field-btn">+ {t("em_add_field")}</button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {fields.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-xl px-3 py-2 text-sm">
                  <span className="font-bold text-slate-700 flex-1">{f.name}</span>
                  <button onClick={() => setFieldLevel(f)} title={t("em_field_level_hint")}
                    className={`text-[10px] font-black rounded-full px-2 py-1 transition ${f.alert_level === "critical" ? "bg-rose-100 text-rose-600" : "bg-slate-200 text-slate-500 hover:bg-slate-300"}`}
                    data-testid={`em-field-level-${f.id}`}>
                    {f.alert_level === "critical" ? `🚨 ${t("em_field_level_critical")}` : t("em_field_level_normal")}
                  </button>
                  <button onClick={() => delField(f.id)} className="text-rose-500 hover:bg-rose-50 rounded-lg p-1" data-testid={`em-del-field-${f.id}`}><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EMEmployees({ t }) {
  const { user } = useAuth();
  const { can } = usePerms();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fAm, setFAm] = useState("");
  const [fMum, setFMum] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [fNat, setFNat] = useState("");
  const [fPos, setFPos] = useState("");
  const [fullEditing, setFullEditing] = useState(null); // full employee doc for EmployeeForm
  const [viewing, setViewing] = useState(null); // {doc, row}
  const [attachingFor, setAttachingFor] = useState(null); // {id, name, attachments}
  const [branchMap, setBranchMap] = useState({});
  const [branchList, setBranchList] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [lastFile, setLastFile] = useState(null);
  const [amPeers, setAmPeers] = useState([]);
  const [buildAm, setBuildAm] = useState("");
  const [buildTarget, setBuildTarget] = useState("");
  const [building, setBuilding] = useState(false);
  const fileRef = useRef();
  const isSuper = user?.role === "super_admin";
  const canImport = isSuper || user?.role === "area_manager";

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get("/em/employees");
      setRows(res.data || []);
    } catch { toast.error(t("em_load_error")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    client.get("/branches").then((r) => {
      const m = {};
      (r.data || []).forEach((b) => { m[b.id] = b; });
      setBranchMap(m);
      setBranchList(r.data || []);
    }).catch(() => {});
    client.get("/admins").then((r) => setAdmins(r.data || [])).catch(() => {});
  }, []);

  const openFullEdit = async (row) => {
    try {
      const res = await client.get(`/employees/${row.id}`);
      setFullEditing({ doc: res.data, em: { company: row.company || "", nationality: row.nationality || "", old_id: row.old_id || "", em_note: row.note || "", branch_city: row.city || "", branch_type: row.branch_type || "" } });
    } catch { toast.error(t("em_load_error")); }
  };

  const archiveEmployee = async (emp) => {
    if (!window.confirm(t("em_archive_confirm"))) return;
    const reason = window.prompt(t("em_archive_reason_prompt"), "") || "";
    try {
      await client.delete(`/employees/${emp.id}`, { params: reason.trim() ? { reason: reason.trim() } : {} });
      toast.success(t("em_archived_ok"));
      setViewing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };

  const openDetails = async (r) => {
    try {
      const res = await client.get(`/employees/${r.id}`);
      setViewing({ doc: res.data, row: r });
    } catch { toast.error(t("em_load_error")); }
  };

  const opts = useMemo(() => {
    const u = (key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))].sort();
    return { am: u("am"), mum: u("mum"), branch: u("branch_name"), company: u("company"), nat: u("nationality"), pos: u("position") };
  }, [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (!fAm || r.am === fAm) && (!fMum || r.mum === fMum) && (!fBranch || r.branch_name === fBranch) &&
      (!fCompany || r.company === fCompany) && (!fNat || r.nationality === fNat) && (!fPos || r.position === fPos) &&
      (!s || r.name.toLowerCase().includes(s) || r.new_id.includes(s) || r.old_id.includes(s) || r.iqama.includes(s) || (r.position || "").toLowerCase().includes(s))
    );
  }, [rows, q, fAm, fMum, fBranch, fCompany, fNat, fPos]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/em/staff-update/xlsx`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "Staff Update.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t("em_download_done"));
    } catch { toast.error(t("em_download_error")); }
    finally { setDownloading(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setLastFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post("/em/staff-update/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(t("em_import_done", { matched: res.data.matched, updated: res.data.updated, branches: res.data.branches_updated }), { duration: 8000 });
      setImportReport(res.data);
      setBuildAm(res.data.am_values?.[0]?.name || "");
      setBuildTarget("");
      if (res.data.am_values?.length && isSuper) {
        client.get("/area-managers/peers").then((r) => setAmPeers(r.data || [])).catch(() => {});
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("em_import_error"));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doBuild = async () => {
    if (!lastFile || !buildAm) return;
    if (isSuper && !buildTarget) { toast.error(t("em_build_target_am")); return; }
    if (!window.confirm(t("em_build_confirm", { am: buildAm }))) return;
    setBuilding(true);
    try {
      const fd = new FormData();
      fd.append("file", lastFile);
      fd.append("am_name", buildAm);
      if (isSuper) fd.append("target_am_id", buildTarget);
      const res = await client.post("/em/staff-update/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(t("em_build_done_title", { am: buildAm }), { duration: 8000 });
      setImportReport(res.data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("em_import_error"));
    } finally {
      setBuilding(false);
    }
  };

  const sel = "border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white max-w-[150px]";
  return (
    <div className="space-y-4" data-testid="em-employees-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
            <Users className="text-sky-600" size={20} /> {t("em_nav_employees")}
            <span className="text-xs font-bold bg-yellow-200 text-yellow-800 rounded-full px-2.5 py-1" data-testid="em-total-count">{loading ? "…" : filtered.length}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {can("add_employees") && (
            <button onClick={() => setFullEditing({ doc: null, em: {} })} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-sky-600 to-blue-700 text-white text-xs font-bold rounded-xl px-3 py-2 shadow hover:opacity-90" data-testid="em-add-employee-btn">
              <Plus size={14} /> {t("em_add_employee")}
            </button>
          )}
          {canImport && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} data-testid="em-import-input" />
              <button onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-1.5 bg-white border border-sky-300 text-sky-700 text-xs font-bold rounded-xl px-3 py-2 hover:bg-sky-50 disabled:opacity-50" data-testid="em-import-btn">
                <Upload size={14} /> {importing ? t("loading") : t("em_import_btn")}
              </button>
            </>
          )}
          <button onClick={download} disabled={downloading} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-xs font-bold rounded-xl px-3.5 py-2 shadow hover:opacity-90 disabled:opacity-50" data-testid="em-download-btn">
            <Download size={14} /> {downloading ? t("loading") : t("em_download_btn")}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute top-1/2 -translate-y-1/2 start-2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("em_search_ph")} className="border border-slate-200 rounded-lg ps-7 pe-2 py-1.5 text-xs bg-white w-52" data-testid="em-search" />
        </div>
        {isSuper && (
          <select value={fAm} onChange={(e) => setFAm(e.target.value)} className={sel} data-testid="em-filter-am">
            <option value="">{t("em_all_am")}</option>
            {opts.am.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}
        <select value={fMum} onChange={(e) => setFMum(e.target.value)} className={sel} data-testid="em-filter-mum">
          <option value="">{t("em_all_mum")}</option>
          {opts.mum.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={fBranch} onChange={(e) => setFBranch(e.target.value)} className={sel} data-testid="em-filter-branch">
          <option value="">{t("em_all_branches")}</option>
          {opts.branch.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={fPos} onChange={(e) => setFPos(e.target.value)} className={sel} data-testid="em-filter-pos">
          <option value="">{t("em_all_positions")}</option>
          {opts.pos.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={fCompany} onChange={(e) => setFCompany(e.target.value)} className={sel} data-testid="em-filter-company">
          <option value="">{t("em_all_companies")}</option>
          {opts.company.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={fNat} onChange={(e) => setFNat(e.target.value)} className={sel} data-testid="em-filter-nat">
          <option value="">{t("em_all_nats")}</option>
          {opts.nat.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl card-shadow overflow-x-auto">
        <table className="w-full text-xs" data-testid="em-employees-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
              {["SN", "CODE", t("em_col_branch"), "B Type", "City", "MUM", "AM", "NEW ID", t("em_col_name"), t("em_col_position"), "COMPANY", "NATIONALITY", "IQAMA", "DATE", "NOTE"].map((h) => (
                <th key={h} className="p-2 text-start font-bold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={15} className="p-8 text-center text-slate-400">{t("loading")}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={15} className="p-8 text-center text-slate-400" data-testid="em-empty">{t("em_no_matches")}</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={r.id} onClick={() => openDetails(r)} className="border-b border-slate-50 cursor-pointer hover:bg-sky-50/60" data-testid={`em-row-${r.id}`}>
                <td className="p-2 text-slate-400">{i + 1}</td>
                <td className="p-2 font-bold text-slate-600 whitespace-nowrap">{r.code}</td>
                <td className="p-2 whitespace-nowrap">{r.branch_name}</td>
                <td className="p-2 whitespace-nowrap">{r.branch_type}</td>
                <td className="p-2 whitespace-nowrap">{r.city}</td>
                <td className="p-2 whitespace-nowrap">{r.mum}</td>
                <td className="p-2 whitespace-nowrap">{r.am}</td>
                <td className="p-2 font-bold" style={{ direction: "ltr" }}>{r.new_id}</td>
                <td className="p-2 font-semibold text-slate-800 whitespace-nowrap">{r.name}</td>
                <td className="p-2 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    /barista/i.test(r.position) ? "bg-green-100 text-green-800"
                    : /branch.?manager/i.test(r.position) ? "bg-orange-100 text-orange-800"
                    : /shift leader/i.test(r.position) ? "bg-yellow-100 text-yellow-800"
                    : "bg-slate-100 text-slate-600"}`}>{r.position || "—"}</span>
                </td>
                <td className="p-2 whitespace-nowrap">{r.company}</td>
                <td className="p-2 whitespace-nowrap">{r.nationality}</td>
                <td className="p-2" style={{ direction: "ltr" }}>{r.iqama}</td>
                <td className="p-2 whitespace-nowrap" style={{ direction: "ltr" }}>{r.date}</td>
                <td className="p-2 max-w-[140px] truncate" title={r.note}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && (
        <EmployeeDetails
          employee={viewing.doc}
          branch={branchMap[viewing.doc.branch_id]}
          adminName={viewing.row.mum}
          hideCerts
          extraSections={<EMStaffSection row={viewing.row} t={t} isSuper={isSuper} onFlagsChanged={(id, flags) => setRows((prev) => prev.map((x) => (x.id === id ? { ...x, em_flags: flags } : x)))} />}
          onClose={() => setViewing(null)}
          onEdit={() => { const row = viewing.row; setViewing(null); openFullEdit(row); }}
          onDelete={() => archiveEmployee(viewing.row)}
          onOpenAttachments={(emp) => { setViewing(null); setAttachingFor({ id: emp.id, name: emp.name, code: viewing.row.new_id, attachments: emp.attachments || [] }); }}
        />
      )}

      {attachingFor && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="em-attachments-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-black text-slate-800 text-sm">{attachingFor.name} <span className="text-slate-400 text-xs">[{attachingFor.code}]</span></h3>
              <button onClick={() => setAttachingFor(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="em-attachments-close"><X size={16} /></button>
            </div>
            <div className="p-5">
              <AttachmentsPanel employeeId={attachingFor.id} attachments={attachingFor.attachments} onChange={(u) => setAttachingFor((s2) => ({ ...s2, attachments: u }))} />
            </div>
          </div>
        </div>
      )}

      {importReport && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setImportReport(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="em-import-report-modal">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-black text-slate-800">📥 {t("em_import_report_title")}</h3>
              <button onClick={() => setImportReport(null)} className="text-slate-400 hover:text-slate-600" data-testid="em-import-report-close">✕</button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-800 font-semibold">{t("em_import_hint")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["matched", "em_import_stat_matched", "text-sky-600"], ["filled", "em_import_stat_filled", "text-emerald-600"], ["updated", "em_import_stat_updated", "text-teal-600"], ["branches_updated", "em_import_stat_branches", "text-indigo-600"]].map(([k, lbl, clr]) => (
                  <div key={k} className="bg-slate-50 rounded-xl p-3 text-center" data-testid={`em-import-stat-${k}`}>
                    <div className={`text-xl font-black ${clr}`}>{importReport[k] ?? 0}</div>
                    <div className="text-[10px] font-bold text-slate-500">{t(lbl)}</div>
                  </div>
                ))}
              </div>
              {(importReport.conflicts_count || 0) === 0 && (importReport.unmatched_count || 0) === 0 && (
                <div className="text-center text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl py-3 font-semibold" data-testid="em-import-no-issues">{t("em_import_no_issues")}</div>
              )}
              {(importReport.conflicts_count || 0) > 0 && (
                <div>
                  <div className="text-sm font-black text-amber-800">⚠️ {t("em_import_conflicts_title", { n: importReport.conflicts_count })}</div>
                  <div className="text-[11px] text-slate-500 mb-2">{t("em_import_conflicts_hint")}</div>
                  <div className="border border-amber-200 rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-50 text-amber-900">
                        <tr>
                          <th className="p-2 text-start font-black">{t("em_conf_emp")}</th>
                          <th className="p-2 text-start font-black">{t("em_conf_field")}</th>
                          <th className="p-2 text-start font-black">{t("em_conf_current")}</th>
                          <th className="p-2 text-start font-black">{t("em_conf_file")}</th>
                        </tr>
                      </thead>
                      <tbody data-testid="em-import-conflicts-table">
                        {importReport.conflicts.map((c, i) => (
                          <tr key={i} className="border-t border-amber-100">
                            <td className="p-2 font-bold text-slate-700">{c.name} <span className="text-slate-400">[{c.new_id}]</span></td>
                            <td className="p-2">{{ old_id: "OLD ID", company: "COMPANY", nationality: "NATIONALITY", em_note: "NOTE", city: "City", branch_type: "B Type" }[c.field] || c.field}</td>
                            <td className="p-2 text-teal-700 font-semibold">{c.current}</td>
                            <td className="p-2 text-rose-600">{c.file}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(importReport.unmatched_count || 0) > 0 && (
                <div>
                  <div className="text-sm font-black text-rose-800">🚫 {t("em_import_unmatched_title", { n: importReport.unmatched_count })}</div>
                  <div className="text-[11px] text-slate-500 mb-2">{t("em_import_unmatched_hint")}</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="em-import-unmatched-list">
                    {importReport.unmatched.map((u, i) => (
                      <span key={i} className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-2 py-1 text-[11px] font-semibold">{u.name || "—"} [{u.new_id}]</span>
                    ))}
                  </div>
                </div>
              )}
              {importReport.build && (
                <div className="border-2 border-emerald-200 bg-emerald-50/50 rounded-xl p-4 space-y-3" data-testid="em-build-result">
                  <div className="text-sm font-black text-emerald-800">✅ {t("em_build_done_title", { am: importReport.build.am_name })}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[["created_employees", "em_build_stat_employees", "text-emerald-700"], ["created_branches", "em_build_stat_branches", "text-teal-700"], ["created_supervisors", "em_build_stat_supervisors", "text-sky-700"], ["skipped_rows", "em_build_stat_skipped", "text-slate-500"]].map(([k, lbl, clr]) => (
                      <div key={k} className="bg-white rounded-xl p-3 text-center" data-testid={`em-build-stat-${k}`}>
                        <div className={`text-xl font-black ${clr}`}>{importReport.build[k] ?? 0}</div>
                        <div className="text-[10px] font-bold text-slate-500">{t(lbl)}</div>
                      </div>
                    ))}
                  </div>
                  {(importReport.build.foreign_count || 0) > 0 && (
                    <div>
                      <div className="text-xs font-black text-amber-800">⚠️ {t("em_build_foreign_title", { n: importReport.build.foreign_count })}</div>
                      <div className="text-[11px] text-slate-500 mb-1.5">{t("em_build_foreign_hint")}</div>
                      <div className="flex flex-wrap gap-1.5" data-testid="em-build-foreign-list">
                        {importReport.build.foreign.map((f, i) => (
                          <span key={i} className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2 py-1 text-[11px] font-semibold">{f.name || "—"} [{f.new_id}] — {f.am}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!importReport.build && (importReport.am_values?.length || 0) > 0 && (
                <div className="border-2 border-sky-200 bg-sky-50/60 rounded-xl p-4 space-y-3" data-testid="em-build-section">
                  <div className="text-sm font-black text-sky-800">🏗️ {t("em_build_title")}</div>
                  <div className="text-[11px] text-slate-600">{t("em_build_hint")}</div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 block mb-1">{t("em_build_select_am")}</label>
                      <select value={buildAm} onChange={(e) => setBuildAm(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white" data-testid="em-build-am-select">
                        {importReport.am_values.map((a) => (
                          <option key={a.name} value={a.name}>{a.name} — {a.rows} {t("em_build_rows")}</option>
                        ))}
                      </select>
                    </div>
                    {isSuper && (
                      <div>
                        <label className="text-[10px] font-bold text-slate-500 block mb-1">{t("em_build_target_am")}</label>
                        <select value={buildTarget} onChange={(e) => setBuildTarget(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white" data-testid="em-build-target-am">
                          <option value="">—</option>
                          {amPeers.map((p) => (
                            <option key={p.id} value={p.id}>{p.full_name || p.username}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button onClick={doBuild} disabled={building || !buildAm} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-xs font-bold rounded-xl px-4 py-2 shadow hover:opacity-90 disabled:opacity-50" data-testid="em-build-db-btn">
                      {building ? "…" : `🏗️ ${t("em_build_btn")}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
              <button onClick={() => setImportReport(null)} className="btn btn-secondary text-xs" data-testid="em-import-report-ok">{t("close") || "إغلاق"}</button>
            </div>
          </div>
        </div>
      )}

      {fullEditing && (
        <EmployeeForm
          employee={fullEditing.doc}
          emFields={fullEditing.em}
          branches={branchList}
          admins={admins}
          onClose={() => setFullEditing(null)}
          onSaved={() => { setFullEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EMDashStats({ t }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { client.get("/em/dashboard").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const isSuper = user?.role === "super_admin";
  const row1 = [
    { k: "employees", v: s.employees, label: "em_ov_total_employees", icon: Users, color: "text-sky-600", bg: "bg-sky-50", to: "/employee-management/employees" },
    { k: "branches", v: s.overview?.branches, label: "em_ov_branches", icon: Building2, color: "text-indigo-600", bg: "bg-indigo-50", to: "/branches" },
    { k: "supervisors", v: s.overview?.supervisors, label: "em_ov_supervisors", icon: UserCog, color: "text-violet-600", bg: "bg-violet-50", to: "/admins" },
    ...(isSuper ? [{ k: "area_managers", v: s.overview?.area_managers, label: "em_ov_area_managers", icon: Crown, color: "text-rose-600", bg: "bg-rose-50", to: "/area-managers" }] : []),
  ];
  const row2 = [
    { k: "pending", v: s.pending, label: "em_stat_pending", icon: Hourglass, color: "text-amber-600", bg: "bg-amber-50", to: "/employee-management/approvals", alert: s.pending > 0 },
    { k: "approved_today", v: s.approved_today, label: "em_stat_approved_today", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", to: "/employee-management/approvals?tab=history&status=approved_today" },
    { k: "rejected", v: s.rejected, label: "em_stat_rejected", icon: XCircle, color: "text-rose-600", bg: "bg-rose-50", to: "/employee-management/approvals?tab=history&status=rejected" },
    { k: "on_vacation", v: s.on_vacation, label: "em_stat_on_vacation", icon: Palmtree, color: "text-emerald-600", bg: "bg-emerald-50", to: "/employee-management/vacations" },
  ];
  const StatCard = ({ c, span }) => {
    const Icon = c.icon;
    return (
      <button
        onClick={() => navigate(c.to)}
        className={`relative text-start bg-white rounded-2xl card-shadow p-4 h-full cursor-pointer transition-transform duration-150 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.97] ${c.alert ? "border-2 border-amber-400 animate-pulse-soft" : ""} ${span ? "col-span-2 lg:col-span-1" : ""}`}
        data-testid={`em-stat-${c.k}`}
        title={t(c.label)}
      >
        {c.alert && (
          <span className="absolute top-2 end-2 flex h-3 w-3" data-testid="em-pending-alert-dot">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
          </span>
        )}
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${c.bg} ${c.color} mb-2`}><Icon size={18} /></span>
        <div className={`text-2xl font-black ${c.color}`}>{c.v ?? 0}</div>
        <div className="text-[11px] font-bold text-slate-500 mt-0.5">{t(c.label)}</div>
      </button>
    );
  };
  return (
    <div className="space-y-3">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="em-overview-cards">
      {row1.map((c, i) => <StatCard key={c.k} c={c} span={row1.length % 2 === 1 && i === row1.length - 1} />)}
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="em-dash-stats">
      {row2.map((c) => <StatCard key={c.k} c={c} />)}
    </div>
    {s.on_vacation > 0 && s.vacation_details && (
      <div className="bg-white rounded-2xl card-shadow p-4" data-testid="em-vacation-stats-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="font-black text-slate-800 text-sm flex items-center gap-2">
            🌴 {t("em_on_vacation_now")}
            <span className="text-xs font-bold bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-0.5" data-testid="em-vacation-stats-count">{s.on_vacation}</span>
          </h2>
          <button onClick={() => navigate("/employee-management/vacations")} className="text-[11px] font-bold text-sky-600 hover:underline" data-testid="em-vacation-stats-all">{t("em_vacation_view_all")} ←</button>
        </div>
        {s.vacation_details.by_am?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="em-vacation-by-am">
            {s.vacation_details.by_am.map((a) => (
              <span key={a.am_name} className="text-[11px] font-bold bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">
                {a.am_name}: <span className="text-emerald-700">{a.count}</span>
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(s.vacation_details.upcoming || []).map((r) => {
            const overdue = r.return_expected && r.return_expected < new Date().toISOString().slice(0, 10);
            return (
            <div key={r.employee_id} className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 border ${overdue ? "bg-rose-50 border-rose-300" : "bg-emerald-50/60 border-emerald-100"}`} data-testid={`em-vacation-upcoming-${r.employee_id}`}>
              <div className="min-w-0">
                <div className={`text-xs font-bold truncate ${overdue ? "text-rose-700" : "text-slate-800"}`}>
                  {overdue && "🔴 "}{r.name}
                </div>
                {overdue ? <div className="text-[10px] font-black text-rose-600">{t("em_vacation_overdue")}</div>
                  : r.am_name && <div className="text-[10px] text-slate-400 truncate">{r.am_name}</div>}
              </div>
              <div className="text-end shrink-0">
                <div className={`text-[10px] ${overdue ? "text-rose-400" : "text-slate-500"}`}>{t("em_return_date")}</div>
                <div className={`text-xs font-black ${overdue ? "text-rose-600" : "text-emerald-700"}`} dir="ltr">{r.return_expected || "—"}</div>
                {r.vacation_start && r.return_expected && new Date(r.return_expected) > new Date(r.vacation_start) && (
                  <div className="text-[10px] font-bold text-slate-500">
                    {t("em_vac_duration", { n: Math.round((new Date(r.return_expected) - new Date(r.vacation_start)) / 86400000) })}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    )}
    </div>
  );
}


export default function EmployeeManagement() {
  const { t } = useT();
  const { user } = useAuth();
  const { section } = useParams();
  const active = EM_SECTIONS.find((s) => s.key === section) || EM_SECTIONS[0];

  if (active.key === "employees") {
    return <EMEmployees t={t} />;
  }
  if (active.key === "transfers" || active.key === "promotions" || active.key === "vacations") {
    return <EMRequestsPage kind={active.key} />;
  }
  if (active.key === "approvals") return <EMApprovals />;
  if (active.key === "reports") return <EMArchive />;
  if (active.key === "archive") return <EMEmployeeArchive />;
  if (active.key === "audit") return <EMAudit />;
  if (active.key === "files") return <EMFiles />;
  if (active.key === "settings") {
    return (
      <div className="max-w-2xl space-y-4" data-testid="em-settings-page">
        <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
          <Settings className="text-sky-600" size={20} /> {t("em_nav_settings")} <TrialBadge t={t} />
        </h1>
        {user?.role === "super_admin" ? (
          <div className="bg-white rounded-2xl card-shadow p-5">
            <h2 className="font-black text-slate-800 text-sm mb-3">{t("ss_dynamic_fields")}</h2>
            <EMFieldsManager compact />
          </div>
        ) : (
          <div className="bg-white rounded-2xl card-shadow p-8 text-center text-slate-400 text-sm" data-testid="em-settings-restricted">{t("em_settings_super_only")}</div>
        )}
      </div>
    );
  }

  if (active.key !== "dashboard") {
    return (
      <div className="max-w-4xl mx-auto" data-testid="em-page">
        <PlaceholderCard section={active} t={t} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="em-dashboard">
      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
          <Users className="text-sky-600" size={24} /> {t("em_module_name")} <TrialBadge t={t} />
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("em_dash_intro")}</p>
      </div>

      <EMDashStats t={t} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {EM_SECTIONS.slice(1).map((s) => (
          <NavLink
            key={s.key}
            to={s.path}
            className="group bg-white rounded-2xl card-shadow p-5 flex items-center gap-4 hover:shadow-xl hover:-translate-y-0.5 transition-all border border-transparent hover:border-sky-300"
            data-testid={`em-card-${s.key}`}
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow group-hover:scale-110 transition-transform shrink-0">
              <s.icon size={22} />
            </div>
            <div>
              <div className="font-black text-slate-800 text-sm">{t(s.labelKey)}</div>
              {["promotions", "reports"].includes(s.key) && (
                <div className="text-[11px] text-slate-400 mt-0.5">{t("em_placeholder_badge")}</div>
              )}
            </div>
          </NavLink>
        ))}
        {user?.role !== "supervisor" && (
          <NavLink
            to="/admins"
            className="group bg-white rounded-2xl card-shadow p-5 flex items-center gap-4 hover:shadow-xl hover:-translate-y-0.5 transition-all border border-transparent hover:border-violet-300"
            data-testid="em-card-supervisors"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow group-hover:scale-110 transition-transform shrink-0">
              <UserCog size={22} />
            </div>
            <div>
              <div className="font-black text-slate-800 text-sm">{t("nav_admins")}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{t("em_supervisors_card_hint")}</div>
            </div>
          </NavLink>
        )}
      </div>
    </div>
  );
}
